import type { System } from '@/core/system';
import type { Engine } from '@/core/engine';
import type { Time } from '@/core/time';
import { City } from '@/world/city';
import { PlayerAnalyzer } from '@/ai/player-analyzer';
import { PREFERENCE_KEYS } from '@/core/types';
import { DEBUG } from '@/config';
import { IS_TOUCH } from '@/utils/device';
import { getStage } from '@/utils/viewport';

/**
 * All diegetic-adjacent 2D: the click-to-enter veil, a whisper of a crosshair, the
 * rare lines of narration, and a developer HUD (hidden by default, toggled with the
 * backtick key) that exposes the city's current read of you. The UI only listens to
 * events — it never reaches into the simulation — so it can be restyled or removed
 * without consequence.
 *
 * The visual language matches the world: paper-white, thin type, lots of silence.
 */
export class UISystem implements System {
  readonly name = 'ui';

  private root!: HTMLDivElement;
  private veil!: HTMLDivElement;
  private hush!: HTMLDivElement;
  private crosshair!: HTMLDivElement;
  private narration!: HTMLDivElement;
  private epilogue!: HTMLDivElement;
  private hud!: HTMLDivElement;
  private bars = new Map<string, HTMLDivElement>();
  private hudVisible = DEBUG.hudDefault;
  private narrationTimer = 0;

  constructor(
    private engine: Engine,
    private city: City,
    private analyzer: PlayerAnalyzer,
  ) {}

  init(): void {
    this.buildDom();
    this.engine.events.on('app:pointerlock', ({ locked }) => {
      this.veil.style.opacity = locked ? '0' : '1';
      this.crosshair.style.opacity = locked ? '0.5' : '0';
    });
    this.engine.events.on('narrative:beat', ({ text, id }) => {
      if (text) this.showNarration(text, id === 'mirror');
      // After the finale beat has spoken and faded, a persistent whisper settles in
      // at the foot of the frame — the gallery placard for the piece.
      if (id === 'finale') {
        window.setTimeout(() => {
          this.epilogue.style.opacity = '0.4';
        }, 14000);
      }
    });
    window.addEventListener('keydown', this.onKey);
  }

  private onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Backquote') {
      this.hudVisible = !this.hudVisible;
      this.hud.style.opacity = this.hudVisible ? '1' : '0';
    }
  };


  update(time: Time): void {
    if (this.narrationTimer > 0) {
      this.narrationTimer -= time.delta;
      if (this.narrationTimer <= 0) this.narration.style.opacity = '0';
    }
    if (this.hudVisible) this.updateHud(time);
  }

  private showNarration(text: string, climactic: boolean): void {
    this.narration.innerHTML = text.replace(/\n/g, '<br/>');
    this.narration.style.opacity = '1';
    this.narration.style.fontSize = climactic ? '2.4rem' : '1.35rem';
    this.narration.style.letterSpacing = climactic ? '0.14em' : '0.06em';
    this.narrationTimer = climactic ? 12 : 6.5;
    if (climactic) {
      // The mirror moment: hush the whole frame with a dedicated (textless) veil
      // so the intro title never reappears.
      this.hush.style.opacity = '1';
      window.setTimeout(() => {
        this.hush.style.opacity = '0';
      }, 5600);
    }
  }

  private updateHud(time: Time): void {
    const prefs = this.analyzer.getPreferences();
    for (const key of PREFERENCE_KEYS) {
      const bar = this.bars.get(key);
      if (bar) bar.style.width = `${Math.round(prefs[key] * 100)}%`;
    }
    const snap = this.analyzer.getSnapshot();
    const info = this.hud.querySelector('.echo-hud-info') as HTMLDivElement;
    if (info) {
      info.textContent =
        `fps ${time.fps.toFixed(0)}  ·  maturity ${(this.city.maturity * 100).toFixed(0)}%  ·  ` +
        `vitality ${(this.city.vitality * 100).toFixed(0)}%  ·  ` +
        `explored ${snap?.cellsVisited ?? 0}`;
    }
  }

  /* -------- DOM construction -------- */

  private buildDom(): void {
    const root = document.createElement('div');
    // Fills #stage, which viewport.ts has sized to the usable area below the status
    // bar, so narration and the crosshair already centre in the visible space.
    root.style.cssText =
      'position:fixed;inset:0;pointer-events:none;font-family:Inter,Helvetica Neue,system-ui,sans-serif;color:#2b2b2b;z-index:10;';
    this.root = root;

    // Click-to-enter veil.
    const veil = document.createElement('div');
    // pointer-events:none is deliberate — clicks must pass through the veil to the
    // WebGL canvas beneath it, which owns the pointer-lock request.
    veil.style.cssText =
      'position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;' +
      'background:rgba(233,231,226,0.92);transition:opacity 1.4s ease;pointer-events:none;gap:1.2rem;';
    // The title stays fixed; only the control hints change for touch devices, which
    // have no mouse/keyboard and enter with a tap rather than a click.
    const enterHint = IS_TOUCH ? 'TAP TO ENTER' : 'CLICK TO ENTER';
    const controlHint = IS_TOUCH
      ? 'LEFT · LOOK — RIGHT · MOVE — TAP TO ENTER'
      : 'WASD MOVE · SHIFT RUN · SPACE JUMP · MOUSE LOOK · ` DEBUG';
    veil.innerHTML =
      '<div style="font-size:3rem;font-weight:200;letter-spacing:0.42em;padding-left:0.42em;">PALIMPSEST</div>' +
      `<div style="font-size:0.9rem;font-weight:300;letter-spacing:0.28em;opacity:0.55;">${enterHint}</div>` +
      '<div style="position:absolute;bottom:2.4rem;font-size:0.7rem;letter-spacing:0.18em;opacity:0.4;font-weight:300;">' +
      `${controlHint}</div>`;
    this.veil = veil;
    root.appendChild(veil);

    // Climax hush layer — a textless paper wash used only for the mirror beat.
    const hush = document.createElement('div');
    hush.style.cssText =
      'position:absolute;inset:0;background:rgba(233,231,226,0.34);opacity:0;transition:opacity 3.2s ease;pointer-events:none;';
    this.hush = hush;
    root.appendChild(hush);

    // Crosshair.
    const cross = document.createElement('div');
    cross.style.cssText =
      'position:absolute;left:50%;top:50%;width:4px;height:4px;margin:-2px 0 0 -2px;border-radius:50%;' +
      'background:#2b2b2b;opacity:0;transition:opacity 0.6s ease;';
    this.crosshair = cross;
    root.appendChild(cross);

    // Narration.
    const narr = document.createElement('div');
    narr.style.cssText =
      'position:absolute;left:50%;top:64%;transform:translate(-50%,-50%);' +
      'text-align:center;font-weight:200;line-height:1.5;max-width:80vw;opacity:0;' +
      'transition:opacity 2.2s ease,font-size 2s ease;text-shadow:0 1px 30px rgba(233,231,226,0.9);';
    this.narration = narr;
    root.appendChild(narr);

    // Epilogue whisper — a persistent, non-interactive placard that fades in after
    // the finale and never leaves.
    const epi = document.createElement('div');
    epi.textContent = 'palimpsest — a portrait, painted by walking';
    epi.style.cssText =
      'position:absolute;left:50%;bottom:2.6rem;transform:translateX(-50%);' +
      'font-size:0.78rem;font-weight:300;letter-spacing:0.34em;padding-left:0.34em;' +
      'white-space:nowrap;opacity:0;transition:opacity 4s ease;pointer-events:none;';
    this.epilogue = epi;
    root.appendChild(epi);

    // Debug HUD.
    const hud = document.createElement('div');
    hud.style.cssText =
      'position:absolute;left:1.2rem;top:1.2rem;font-size:0.72rem;font-weight:400;letter-spacing:0.04em;' +
      'background:rgba(255,255,255,0.55);backdrop-filter:blur(6px);padding:0.8rem 1rem;border-radius:8px;' +
      `opacity:${this.hudVisible ? '1' : '0'};transition:opacity 0.4s ease;min-width:220px;`;
    const title = document.createElement('div');
    title.textContent = 'THE CITY THINKS YOU LIKE…';
    title.style.cssText = 'letter-spacing:0.16em;opacity:0.5;margin-bottom:0.6rem;font-size:0.62rem;';
    hud.appendChild(title);
    for (const key of PREFERENCE_KEYS) {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:center;gap:0.5rem;margin:0.22rem 0;';
      const label = document.createElement('span');
      label.textContent = key;
      label.style.cssText = 'width:96px;opacity:0.7;text-transform:capitalize;';
      const track = document.createElement('div');
      track.style.cssText = 'flex:1;height:4px;background:rgba(43,43,43,0.12);border-radius:2px;overflow:hidden;';
      const fill = document.createElement('div');
      fill.style.cssText = 'height:100%;width:0%;background:#2b2b2b;transition:width 0.4s ease;';
      track.appendChild(fill);
      row.appendChild(label);
      row.appendChild(track);
      hud.appendChild(row);
      this.bars.set(key, fill);
    }
    const info = document.createElement('div');
    info.className = 'echo-hud-info';
    info.style.cssText = 'margin-top:0.7rem;opacity:0.55;font-size:0.64rem;letter-spacing:0.02em;';
    hud.appendChild(info);
    this.hud = hud;
    root.appendChild(hud);

    // Into the rotation stage so the UI rotates with the world in forced landscape.
    getStage().appendChild(root);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }
}
